import { TravelRequestsQueryType } from 'api/travel-requests-list';
import { deepComputedFrom } from 'aurelia-deep-computed';
import { autoinject, computedFrom, newInstance } from 'aurelia-framework';
import { Router, RouterConfiguration } from 'aurelia-router';
import { log } from 'framework/script_utilities/dev-decorators';
import { TaskCounter } from 'framework/script_utilities/task-executor';
import { CountryVisit, initCountryVisit } from 'model/forms/travel-request';
import { IdentityService } from 'services/identity-service';
import { KeywordsService } from 'services/keywords-service';
import { aprovalRequired, SafetyService } from 'services/safety-service';
import { requireServices } from 'support/activation';
import { MobileAppService } from 'services/mobile-app-service';

@autoinject
export class TravelRequestsListScreen {
  router: Router;
  countryVisits: CountryVisit[];

  constructor(private identity: IdentityService, private keywords: KeywordsService, private safety: SafetyService, @newInstance() private taskCounter: TaskCounter, private mobileAppService: MobileAppService) { }

  configureRouter(config: RouterConfiguration, router: Router) {
    const routes: any[] = [
      { route: 'waiting', viewPorts: { main: { moduleId: 'screens/travel_requests/lists/requests-common' }, toolbar: { moduleId: 'components/toolbars/clear-filters-toolbar' } }, name: 'requests-waiting', activationStrategy: 'replace', title: 'For Review', nav: true, settings: { queryType: TravelRequestsQueryType.ForReview } },
      { route: 'reviewed', viewPorts: { main: { moduleId: 'screens/travel_requests/lists/requests-common' }, toolbar: { moduleId: 'components/toolbars/clear-filters-toolbar' } }, name: 'requests-reviewed', activationStrategy: 'replace', title: 'Reviewed', nav: true, settings: { queryType: TravelRequestsQueryType.Reviewed } }
    ]
    if (this.mobileAppService.isMobileApp)
      routes.unshift({ route: '', redirect: 'waiting' })
    else {
      routes.unshift([
        { route: '', redirect: 'current' },
        { route: 'current', viewPorts: { main: { moduleId: 'screens/travel_requests/lists/requests-common' }, toolbar: { moduleId: 'components/toolbars/clear-filters-toolbar' } }, name: 'requests-current', activationStrategy: 'replace', title: 'My Current', nav: true, settings: { queryType: TravelRequestsQueryType.Current } },
        { route: 'past', viewPorts: { main: { moduleId: 'screens/travel_requests/lists/requests-common' }, toolbar: { moduleId: 'components/toolbars/clear-filters-toolbar' } }, name: 'requests-past', activationStrategy: 'replace', title: 'My Past', nav: true, settings: { queryType: TravelRequestsQueryType.Past } }
      ]);
      routes.push({ route: 'all', viewPorts: { main: { moduleId: 'screens/travel_requests/lists/requests-common' }, toolbar: { moduleId: 'components/toolbars/clear-filters-toolbar' } }, name: 'requests-all', activationStrategy: 'replace', title: 'For Admin', nav: true, settings: { queryType: TravelRequestsQueryType.All } });
    }
    config.map(routes);
    this.router = router;
  }

  async canActivate() {
    await requireServices(this.identity, this.keywords, this.safety);
  }

  @log('debug')
  activate() {
    this.applyPermissions();
    this.countryVisits = [initCountryVisit()];
  }

  private applyPermissions() {
    this.router.navigation.find(i => i.config.name === 'requests-waiting').settings['hidden'] = !(this.identity.roles.isAdmin || this.identity.roles.isApprover || this.identity.roles.isDelegate);
    this.router.navigation.find(i => i.config.name === 'requests-reviewed').settings['hidden'] = !(this.identity.roles.isAdmin || this.identity.roles.isApprover || this.identity.roles.isDelegate);
    const requestsAllRoute = this.router.navigation.find(i => i.config.name === 'requests-all');
    if (requestsAllRoute != null)
      requestsAllRoute.settings['hidden'] = !this.identity.roles.isAdmin;
  }

  @computedFrom('taskCounter.runningCount')
  get reloading() {
    return this.taskCounter.runningCount > 0;
  }

  @deepComputedFrom('countryVisits'/*, 'safety.countrySettings'*/)
  get approvalRequired() {
    return this.countryVisits.some(v => v.country != null && aprovalRequired(this.safety.countrySettings.get(v.country.id)?.approvalRequirementValue, v.transit));
  }

  createRequest() {
    const initialCountries = this.countryVisits.filter((v, i, all) => v.country != null && !all.slice(0, i).some(c => c.country === v.country));
    let routeOptions = { countryIds: initialCountries.map(x => x.country.id), transits: initialCountries.map(x => x.transit) };
    this.router.navigateToRoute('request-detail', routeOptions);
  }
}
